import merge from 'lodash.merge';
import get from 'lodash.get';
import isPlainObject from 'lodash.isplainobject';
import axios from 'axios';
import JwtBaseClient from './JwtBaseClient';
import ApiError from '../models/ApiError';
/**
 * @class AuctionHouseClient
 * @extends JwtBaseClient
 * @description Client used to interface with AuctionHouse via tyk gateway.
 */
class AuctionHouseClient extends JwtBaseClient {
  constructor(user, baseUri, additionalHeaders) {
    const pathToApiErrors = 'response.data.errors';
    const uri = baseUri || import.meta.env.VITE_AUCTION_HOUSE_BASE_URI;
    const headers = {
      'acv-client-version': import.meta.env.VITE_VERSION,
      'acv-client-type': 'desktop',
      'acv-client-os': 'web',
      'acv-client-timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
      ...additionalHeaders,
    };
    super(uri, pathToApiErrors, headers);
    this.user = user;
    this.auctionHouseRootUri = 'external/v1';
  }

  request(config) {
    const configWithHeaders = config;
    configWithHeaders.headers = merge(this.headers, config.headers);
    configWithHeaders.data = this.addDefaultDealerId(config, 'data');

    return this.axiosInstance.request(configWithHeaders)
      .catch((error) => {
        if (axios.isCancel(error) || error.message === 'canceled') {
          return Promise.reject(error);
        }
        const defaultErrorMessage = 'There was problem processing your request. Please try again or adjust your parameters.';

        let errorMessage = get(
          error,
          this.pathToApiErrors,
          [{
            detail: defaultErrorMessage,
          }],
        );

        if (!errorMessage) {
          // found path but no information, default message (so usages can still infer in catches)
          errorMessage = [{ detail: defaultErrorMessage }];
        }

        const errors = errorMessage.map((e) => new ApiError(e.detail, { ...e, ...error }));
        return Promise.reject(errors);
      });
  }

  addDefaultDealerId(data, dataKey) {
    const { hasMultipleDealerships, defaultDealerId } = this.user;
    if (!hasMultipleDealerships
        && data[dataKey]
        && isPlainObject(data[dataKey])
        && !Object.keys(data[dataKey]).includes('dealer_id')) {
      return {
        ...data[dataKey],
        dealer_id: defaultDealerId || null,
      };
    }
    return data[dataKey];
  }

  getProxyBidTypes() {
    return this.get(`${this.auctionHouseRootUri}/proxy_bid_types`)
      .then((response) => response.data);
  }

  getProxyBidInfo(auctionId) {
    return this.get(`${this.auctionHouseRootUri}/auctions/${auctionId}/proxy_bid_info`)
      .then((response) => response.data);
  }

  submitBid(auctionId, amount, proxy = false, persistent = false) {
    return this.post(
      `${this.auctionHouseRootUri}/auctions/${auctionId}/bid`,
      {
        auction_id: auctionId,
        amount,
        proxy,
        persistent,
      },
      null,
      null,
      6,
    )
      .then((response) => response.data);
  }
}

export default AuctionHouseClient;
